import { useCallback, useEffect, useRef, useState } from 'react';

type Offset = { x: number; y: number };

/**
 * Lightweight drag behaviour: returns a drag-handle pointerdown handler and the accumulated
 * translate offset (in px) to apply on top of a CSS-positioned element.
 *
 * This is a v2 copy of the v1 `JourneyErrorsPopover/useDraggablePopover` hook. v1 hard-codes the
 * `.journey-errors-popover` root class in its `closest()` lookup, so v2 needs its own copy that
 * clamps against the unified `.journey-launch-popover` root instead. v1 stays untouched.
 */
export default function useDraggablePopover() {
  // Keep at least this much of the popover within the flow canvas so it can never
  // be dragged completely out of bounds (and back in).
  const VISIBLE_MARGIN = 40;
  const ROOT_CLASS = '.journey-launch-popover';

  const [offset, setOffset] = useState<Offset>({ x: 0, y: 0 });
  const dragStart = useRef<{
    pointerX: number;
    pointerY: number;
    originX: number;
    originY: number;
    rect: DOMRect;
    bounds: DOMRect;
  } | null>(null);
  const frame = useRef<number | null>(null);

  const onPointerMove = useCallback((e: PointerEvent) => {
    if (!dragStart.current) return;
    const { pointerX, pointerY, originX, originY, rect, bounds } = dragStart.current;
    const deltaX = e.clientX - pointerX;
    const deltaY = e.clientY - pointerY;
    // Clamp against the flow canvas bounds using the element's starting rect, keeping a sliver visible.
    const minX = originX - (rect.left - bounds.left) - rect.width + VISIBLE_MARGIN;
    const maxX = originX + (bounds.right - rect.left) - VISIBLE_MARGIN;
    const minY = originY - (rect.top - bounds.top) - rect.height + VISIBLE_MARGIN;
    const maxY = originY + (bounds.bottom - rect.top) - VISIBLE_MARGIN;
    const nextX = Math.min(Math.max(originX + deltaX, minX), maxX);
    const nextY = Math.min(Math.max(originY + deltaY, minY), maxY);
    if (frame.current) cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(() => setOffset({ x: nextX, y: nextY }));
  }, []);

  const onPointerUp = useCallback(() => {
    dragStart.current = null;
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
    window.removeEventListener('pointercancel', onPointerUp);
  }, [onPointerMove]);

  const onDragHandleMouseDown = useCallback(
    (e: React.PointerEvent) => {
      // ignore drags initiated on interactive children (e.g. the close/launch buttons)
      if ((e.target as HTMLElement).closest('button')) return;
      // The popover's offsetParent is the flow canvas container; clamp against it.
      const popover = e.currentTarget.closest<HTMLElement>(ROOT_CLASS);
      if (!popover) return;
      const canvas = (popover.offsetParent as HTMLElement | null) ?? popover.parentElement ?? null;
      dragStart.current = {
        pointerX: e.clientX,
        pointerY: e.clientY,
        originX: offset.x,
        originY: offset.y,
        rect: e.currentTarget.getBoundingClientRect(),
        bounds: (canvas ?? document.documentElement).getBoundingClientRect(),
      };
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp);
      window.addEventListener('pointercancel', onPointerUp);
    },
    [offset.x, offset.y, onPointerMove, onPointerUp],
  );

  useEffect(
    () => () => {
      if (frame.current) cancelAnimationFrame(frame.current);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
    },
    [onPointerMove, onPointerUp],
  );

  return { offset, onDragHandleMouseDown };
}
